import React, { useEffect, useState, useCallback } from "react";
import styles from "./AccountsBaseComponent.module.css";
import Spinner from "../../../common/Spinner/Spinner";
import { useAuth } from "../../../../context/AuthContext";
import { AccountsAPI, SystemAPI } from "../../../../utils/firebase";
import DataSourcePill from "../../../common/DataSourcePill/DataSourcePill";
import AccountsTableComponent from "./AccountsTableComponent/AccountsTableComponent";
import EditAccountModalComponent from "./EditAccountModalComponent/EditAccountModalComponent";
import AddAccountModalComponent from "./AddAccountModalComponent/AddAccountModalComponent";

const AccountsBaseComponent = () => {
  const { user } = useAuth();
  const email = (user?.email || "").trim();

  const [list, setList] = useState({ loading: true, error: null, items: [], missing: false });
  const [lookups, setLookups] = useState({
    loading: true,
    error: null,
    accountTypes: [],
    currencies: [],
    fromCache: null,
  });

  const [modal, setModal] = useState({ addOpen: false, editOpen: false, editRow: null });

  useEffect(() => {
    if (!email) {
      setList({
        loading: false,
        error: new Error("No user email."),
        items: [],
        missing: false,
      });
      return;
    }
    setList((s) => ({ ...s, loading: true, error: null }));
    let unsub = () => {};
    try {
      unsub = AccountsAPI.subscribeUserAccounts({
        userEmail: email,
        cap: 1000,
        onData: (rows) =>
          setList({ loading: false, error: null, items: rows, missing: false }),
        onError: (err) =>
          setList({
            loading: false,
            error: err,
            items: [],
            missing: /user_accounts|COLLECTIONS|assertCollectionName|collection/i.test(
              String(err?.message || "")
            ),
          }),
      });
    } catch (err) {
      const msg = String(err?.message || "");
      const missing = /user_accounts|COLLECTIONS|assertCollectionName|collection/i.test(msg);
      setList({ loading: false, error: err, items: [], missing });
      return;
    }
    return () => unsub && unsub();
  }, [email]);

  async function loadLookups({ bypassCache = false } = {}) {
    setLookups((s) => ({ ...s, loading: true, error: null }));
    try {
      const [{ value: types, fromCache: f1 }, { value: currencies, fromCache: f2 }] =
        await Promise.all([
          SystemAPI.fetchAccountTypesWithMeta(bypassCache ? { noCache: true } : {}),
          SystemAPI.fetchCurrencyListWithMeta(bypassCache ? { noCache: true } : {}),
        ]);
      setLookups({ loading: false, error: null, accountTypes: types, currencies, fromCache: f1 || f2 });
    } catch (err) {
      setLookups({ loading: false, error: err, accountTypes: [], currencies: [], fromCache: null });
    }
  }
  useEffect(() => { loadLookups(); }, []);

  const handleCreate = useCallback((form) => AccountsAPI.createUserAccount({ user, ...form }), [user]);
  const handleUpdate = useCallback((id, updates) => AccountsAPI.updateUserAccount({ id, updates }), []);

  return (
    <div className="container-fluid">
      <div className="pp-page">
        {/* Header */}
        <div className="pp-page-header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <h1 className="pp-page-title">Accounts</h1>
            <DataSourcePill
              fromCache={lookups.fromCache}
              loading={lookups.loading}
              onRefresh={() => loadLookups({ bypassCache: true })}
            />
          </div>
          <div className="pp-page-actions"></div>
        </div>

        {/* Body */}
        {lookups.error && (
          <div className="alert alert-warning mb-3">
            Failed to load lookups (account types/currencies). Some fields may be unavailable.
          </div>
        )}

        {list.missing && (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <span className="badge text-bg-secondary mb-2">In progress</span>
              <div className="text-muted">
                <code>COLLECTIONS.userAccounts</code> is not defined. Add it in{" "}
                <code>src/utils/firebase/core/collection.js</code>.
              </div>
            </div>
          </div>
        )}

        {!list.missing && list.loading && (
          <div className="py-5 d-flex justify-content-center">
            <Spinner label="Loading accounts..." />
          </div>
        )}

        {!list.missing && !list.loading && list.error && (
          <div className="alert alert-danger" role="alert">
            <strong>Failed to load accounts.</strong>
            <div className="small mt-1">{list.error.message}</div>
          </div>
        )}

        {!list.missing && !list.loading && !list.error && (
          <AccountsTableComponent
            items={list.items}
            onAdd={() => setModal({ addOpen: true, editOpen: false, editRow: null })}
            onEdit={(row) => setModal({ addOpen: false, editOpen: true, editRow: row })}
          />
        )}

        {/* Modals */}
        {modal.addOpen && (
          <AddAccountModalComponent
            onClose={() => setModal({ addOpen: false, editOpen: false, editRow: null })}
            onSave={handleCreate}
            accountTypes={lookups.accountTypes}
            currencies={lookups.currencies}
          />
        )}
        {modal.editOpen && modal.editRow && (
          <EditAccountModalComponent
            row={modal.editRow}
            onClose={() => setModal({ addOpen: false, editOpen: false, editRow: null })}
            onSave={(updates) => handleUpdate(modal.editRow.id, updates)}
            accountTypes={lookups.accountTypes}
            currencies={lookups.currencies}
          />
        )}
      </div>
    </div>
  );
};

export default AccountsBaseComponent;
